const AUTH_KEYS = {
  token: "token",
  userId: "userId",
  user: "user",
};

const LEGACY_PROFILE_KEYS = [
  "session",
  "username",
  "displayname",
  "email",
  "phone",
  "whatsapp",
  "country",
  "city",
  "location",
];

const PROFILE_FIELDS = [
  "userId",
  "username",
  "email",
  "role",
  "phone",
  "whatsapp",
  "country",
  "city",
  "location",
  "imageUrl",
  "profileImage",
  "profileImageUrl",
];

const safeJsonParse = (value, fallback = null) => {
  if (!value) return fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const sanitizeProfile = (user) => {
  if (!user || typeof user !== "object") return null;

  const profile = {};

  PROFILE_FIELDS.forEach((field) => {
    if (user[field] !== undefined) {
      profile[field] = user[field];
    }
  });

  return profile;
};

const readLegacyProfile = () => {
  const userId = localStorage.getItem(AUTH_KEYS.userId);
  const username =
    localStorage.getItem("username") ||
    localStorage.getItem("displayname");
  const email = localStorage.getItem("email");
  const phone = localStorage.getItem("phone");
  const whatsapp = localStorage.getItem("whatsapp");
  const country = localStorage.getItem("country");
  const city = localStorage.getItem("city");
  const location = localStorage.getItem("location");

  if (
    !userId &&
    !username &&
    !email &&
    !phone &&
    !whatsapp &&
    !country &&
    !city &&
    !location
  ) {
    return null;
  }

  return {
    ...(userId ? { userId: Number(userId) || userId } : {}),
    ...(username ? { username } : {}),
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
    ...(whatsapp ? { whatsapp } : {}),
    ...(country ? { country } : {}),
    ...(city ? { city } : {}),
    ...(location ? { location } : {}),
  };
};

export const getStoredToken = () => localStorage.getItem(AUTH_KEYS.token);

export const getStoredUserId = () => {
  const value = Number(localStorage.getItem(AUTH_KEYS.userId));
  return Number.isFinite(value) && value > 0 ? value : null;
};

export const getStoredUser = () => {
  const cached = safeJsonParse(localStorage.getItem(AUTH_KEYS.user));

  if (cached && typeof cached === "object" && !Array.isArray(cached)) {
    return cached;
  }

  return readLegacyProfile();
};

export const getStoredUserLocation = () => {
  const user = getStoredUser();

  return {
    country: String(user?.country || "").trim(),
    city: String(user?.city || "").trim(),
    location: String(user?.location || "").trim(),
  };
};

export const saveStoredUser = (user) => {
  const nextProfile = sanitizeProfile(user);
  if (!nextProfile) return;

  const currentProfile = getStoredUser() || {};
  const merged = {
    ...currentProfile,
    ...nextProfile,
  };

  localStorage.setItem(AUTH_KEYS.user, JSON.stringify(merged));

  if (merged.userId !== undefined && merged.userId !== null) {
    localStorage.setItem(AUTH_KEYS.userId, String(merged.userId));
  }

  // Remove duplicated profile fields left by older frontend versions.
  LEGACY_PROFILE_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const saveAuthSession = (user) => {
  if (!user || typeof user !== "object") return;

  const profile = sanitizeProfile(user) || {};

  // Start each login from a clean profile cache so fields from a previous
  // account can never leak into the newly authenticated account.
  LEGACY_PROFILE_KEYS.forEach((key) => localStorage.removeItem(key));
  localStorage.removeItem(AUTH_KEYS.user);
  localStorage.removeItem(AUTH_KEYS.userId);

  if (user.token) {
    localStorage.setItem(AUTH_KEYS.token, String(user.token));
  } else {
    localStorage.removeItem(AUTH_KEYS.token);
  }

  if (user.userId !== undefined && user.userId !== null) {
    localStorage.setItem(AUTH_KEYS.userId, String(user.userId));
  }

  localStorage.setItem(AUTH_KEYS.user, JSON.stringify(profile));
  window.dispatchEvent(new Event("dealswapify:auth-changed"));
};

const removeSessionKeysForUser = (userId) => {
  if (!userId) return;

  const exactKeys = new Set([
    `inbox_cache_${userId}`,
    `activeChat_${userId}`,
    `chat_read_state_${userId}`,
    `chatCount:${userId}`,
  ]);
  const prefixes = [
    `chat_draft_${userId}_`,
    `chat_history_${userId}_`,
  ];

  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index);
    if (
      key &&
      (exactKeys.has(key) || prefixes.some((prefix) => key.startsWith(prefix)))
    ) {
      sessionStorage.removeItem(key);
    }
  }
};

export const clearAuthSession = () => {
  const userId = localStorage.getItem(AUTH_KEYS.userId);

  [AUTH_KEYS.token, AUTH_KEYS.userId, AUTH_KEYS.user, ...LEGACY_PROFILE_KEYS]
    .forEach((key) => localStorage.removeItem(key));

  // User-scoped chat/UI caches should not survive logout on a shared browser.
  if (userId) {
    [
      `inbox_cache_${userId}`,
      `activeChat_${userId}`,
      `chat_read_state_${userId}`,
      `chatCount:${userId}`,
    ].forEach((key) => localStorage.removeItem(key));
  }

  // Legacy non-scoped keys from older frontend versions.
  ["inbox_cache", "activeChat", "items"].forEach((key) => localStorage.removeItem(key));

  removeSessionKeysForUser(userId);
  window.dispatchEvent(new Event("dealswapify:auth-changed"));
};
